import { Navigate } from "@tanstack/react-location";
import Center from "@/components/elements/layout/Center";
import LoadingSpinner from "@/components/elements/LoadingSpinner";
import { useCurrentUser } from "@/providers/UserProvider";
import {
  noteLaunchDarklyIdentifySkipped,
  useFeatureFlagContext,
} from "@/features/feature-flags";
import { markBootStage } from "@/monitoring/bootMarks";
import { useCallback, useEffect, useState } from "react";
import { connectDWRoute } from "@/router";

import { useCurrentAccount } from "./AccountProvider";

const MAX_IDENTIFY_WAIT_MS = 3000;

export function AccountValidator(props: { children: JSX.Element }) {
  const user = useCurrentUser();
  const account = useCurrentAccount();
  const [featureFlagsReady, setFeatureFlagsReady] = useState(false);
  const { updateContext, clientReady } = useFeatureFlagContext();

  const markFlagsReady = useCallback(() => {
    markBootStage("flags-ready");
    setFeatureFlagsReady(true);
  }, []);

  // Runs again when the client turns up, so a late LaunchDarkly still identifies.
  useEffect(() => {
    if (!clientReady) return;
    let abandoned = false;
    updateContext({
      key: user.id,
      email: user.email,
      accountId: account.id,
      slug: account.slug,
    }).then(() => {
      if (!abandoned) markFlagsReady();
    });
    return () => {
      abandoned = true;
    };
  }, [
    clientReady,
    updateContext,
    user.id,
    user.email,
    account.id,
    account.slug,
    markFlagsReady,
  ]);

  // Bounds the whole gate, not just the wait for a client: a LaunchDarkly that
  // never answers must not hold the workspace back. A late identify still lands.
  useEffect(() => {
    if (featureFlagsReady) return;
    const timer = setTimeout(() => {
      noteLaunchDarklyIdentifySkipped("gave-up-waiting");
      markFlagsReady();
    }, MAX_IDENTIFY_WAIT_MS);
    return () => clearTimeout(timer);
  }, [featureFlagsReady, markFlagsReady]);

  if (!account.dataWarehouseConnectionId) {
    // Redirect to data warehouse setup up if workspace doesn't have a connection set up
    return <Navigate to={connectDWRoute(account.slug)} replace search />;
  }

  if (!featureFlagsReady) {
    return (
      <Center className="absolute inset-0">
        <LoadingSpinner />
      </Center>
    );
  }
  return props.children;
}
